(function () {
  if (window.__editorModalLoaded) return;
  window.__editorModalLoaded = true;

  class EditorModal {
    constructor() {
      this.modal = document.getElementById("editor-modal");
      this.mountPoint = document.getElementById("editor-mount");
      this.closeBtn = this.modal?.querySelector('[is="close-button"]');
      this.loaded = false;
      this.instance = null;
      this.activeWorkers = [];
      this.loadedStyleSheets = [];
      this.disabledStyleSheets = [];

      if (this.modal) {
        this.modal.addEventListener("close", () => this.onModalClose());
      }
    }

    // Disable theme CSS to prevent styles leaking into the editor
    disableThemeStyles() {
      if (this.disabledStyleSheets.length > 0) return;

      const themeStyles = Array.from(document.styleSheets).filter((sheet) => {
        try {
          return (
            sheet.href &&
            !sheet.href.includes("vue-editor") &&
            sheet.href.includes("theme.css")
          );
        } catch (e) {
          return false;
        }
      });

      this.disabledStyleSheets = themeStyles.map((sheet) => ({
        sheet: sheet,
        wasDisabled: sheet.disabled,
      }));

      this.disabledStyleSheets.forEach((item) => {
        item.sheet.disabled = true;
      });
    }

    restoreThemeStyles() {
      this.disabledStyleSheets.forEach((item) => {
        item.sheet.disabled = item.wasDisabled;
      });
      this.disabledStyleSheets = [];
    }

    // Remove dynamically loaded editor CSS on close so it doesn't bleed into the PDP
    removeEditorCSS() {
      this.loadedStyleSheets.forEach((link) => {
        if (link && link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
      this.loadedStyleSheets = [];
    }

    loadCSS(href) {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`link[href="${href}"]`);
        if (existing) {
          this.loadedStyleSheets.push(existing);
          resolve();
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => {
          this.loadedStyleSheets.push(link);
          resolve();
        };
        link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));
        document.head.appendChild(link);
      });
    }

    // Terminate all active workers and revoke blob URLs on close
    terminateWorkers() {
      this.activeWorkers.forEach((worker) => {
        try {
          worker.terminate();
          if (worker.__blobURL) {
            URL.revokeObjectURL(worker.__blobURL);
          }
        } catch (e) {
          console.warn("Failed to terminate worker:", e);
        }
      });
      this.activeWorkers = [];
    }

    // Override Worker constructor to intercept worker creation and track instances
    setupWorkerOverride() {
      const self = this;
      const OriginalWorker = window.__ORIGINAL_WORKER || window.Worker;

      if (!window.__ORIGINAL_WORKER) {
        window.__ORIGINAL_WORKER = OriginalWorker;
      }

      window.Worker = class extends OriginalWorker {
        constructor(scriptURL, options) {
          if (scriptURL && typeof scriptURL === "string") {
            const isWorker =
              scriptURL.includes("worker.") && window.WORKER_PATH;
            const isTextureWorker =
              scriptURL.includes("textureWorker") && window.TEXTURE_WORKER_PATH;

            if (isWorker || isTextureWorker) {
              const workerPath = isTextureWorker
                ? window.TEXTURE_WORKER_PATH
                : window.WORKER_PATH;
              const workerCode = `import "${workerPath}";`;
              const blob = new Blob([workerCode], { type: "text/javascript" });
              const blobURL = URL.createObjectURL(blob);

              super(blobURL, { type: "module" });

              self.activeWorkers.push(this);
              this.__blobURL = blobURL;
              return;
            }
          }
          super(scriptURL, options);
        }
      };
    }

    onModalClose() {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      window.scrollTo(0, this.scrollPosition);
      this.restoreThemeStyles();
      const closeBtn = this.modal.querySelector('[is="close-button"]');

      // Clean up editor query params and hash from URL on close
      const url = new URL(window.location.href);
      [
        "locationQuery",
        "dateQuery",
        "productId",
        "selectedSize",
        "printType",
        "frameColor",
        "color",
        "lineItemKey",
        "openEditor",
      ].forEach((key) => url.searchParams.delete(key));
      url.hash = url.hash.startsWith("#/") ? "" : url.hash;
      history.replaceState(null, "", url);

      this.terminateWorkers();
      this.removeEditorCSS();

      // Unmount Vue instance and clear mount point
      if (this.instance) {
        try {
          if (typeof this.instance.unmount === "function") {
            this.instance.unmount();
          } else if (typeof this.instance.$destroy === "function") {
            this.instance.$destroy();
          }
        } catch (e) {
          console.warn("Editor cleanup error:", e);
        }
        this.instance = null;
      }

      if (this.mountPoint) {
        this.mountPoint.innerHTML = "";
      }
      if (closeBtn) {
        this.mountPoint.appendChild(closeBtn);
      }
      const loader = this.modal.querySelector("#editor-loading");
      if (loader) {
        loader.classList.remove("is-hidden");
        loader.style.animation = "none";
        loader.offsetHeight; // force reflow to restart animation
        loader.style.animation = "editor-loading-appear 0s 0.3s forwards";
      }
      this.loaded = false;
    }

    async open() {
      if (this.isOpening) return;
      this.isOpening = true;

      try {
        if (!this.modal || !this.mountPoint) {
          console.error("Editor modal not found");
          return;
        }

        this.terminateWorkers();
        this.modal.classList.remove("is-open", "is-closing");
        this.scrollPosition = window.scrollY;
        this.modal.showModal();
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        setTimeout(() => {
          this.modal.classList.add("is-open");
        }, 10);

        this.disableThemeStyles();

        try {
          await this.loadAssets();
          this.loaded = true;
          await this.initEditor();
        } catch (error) {
          console.error("Failed to initialize editor:", error);
        } finally {
          if (!this.modal.open) {
            this.restoreThemeStyles();
          }
        }
      } finally {
        this.isOpening = false;
      }
    }

    async loadAssets() {
      const { assets } = window.EDITOR_DATA;

      const cssFiles = [assets.vendorCss, assets.sharedCss, assets.css];
      for (const cssFile of cssFiles) {
        await this.loadCSS(cssFile);
      }

      // Setup worker override only if editor requires a worker
      if (assets.worker) {
        window.WORKER_PATH = assets.worker;
        this.setupWorkerOverride();
      }
      if (assets.textureWorker) {
        window.TEXTURE_WORKER_PATH = assets.textureWorker;
        this.setupWorkerOverride();
      }

      window.products = window.products || {};
      window.products[window.EDITOR_DATA.productId] = {
        ...window.EDITOR_DATA.product,
        google_item_id: window.EDITOR_DATA.googleItemId,
      };
    }

    async initEditor() {
      window.products = window.products || {};
      const productId = window.EDITOR_DATA.productId;
      window.products[productId] = window.EDITOR_DATA.product;

      if (this.instance) {
        try {
          if (typeof this.instance.unmount === "function") {
            this.instance.unmount();
          } else if (typeof this.instance.$destroy === "function") {
            this.instance.$destroy();
          }
        } catch (e) {
          console.warn("Failed to unmount:", e);
        }
        this.instance = null;
      }

      // Replace entire mount point to give Vue a completely fresh DOM node
      const newMount = document.createElement("div");
      newMount.id = "editor-mount";
      newMount.className = "editor-modal__mount";
      this.mountPoint.replaceWith(newMount);
      this.mountPoint = newMount;
      if (this.closeBtn) {
        this.mountPoint.appendChild(this.closeBtn);
      }

      const appDiv = document.createElement("div");
      appDiv.id = "app";
      this.mountPoint.appendChild(appDiv);
      await new Promise((resolve) => setTimeout(resolve, 300));

      try {
        await this._mountEditor();
      } catch (error) {
        if (error.message?.includes("nextSibling")) {
          console.warn("Mount failed, retrying...");
          this.mountPoint.innerHTML = "";
          await new Promise((resolve) => setTimeout(resolve, 200));
          this.mountPoint.innerHTML = '<div id="app"></div>';
          await new Promise((resolve) => setTimeout(resolve, 100));
          await this._mountEditor();
        } else {
          throw error;
        }
      }
    }

    async _mountEditor() {
      // Ensure product data is available before editor initializes
      window.products = window.products || {};
      window.products[window.EDITOR_DATA.productId] = {
        ...window.EDITOR_DATA.product,
        google_item_id: window.EDITOR_DATA.googleItemId,
      };

      const { assets } = window.EDITOR_DATA;
      const timestamp = Date.now();

      await Promise.all([
        import(`${assets.vendorJs}#${timestamp}`),
        import(`${assets.sharedJs}#${timestamp}`),
        import(`${assets.js}#${timestamp}`),
      ]);

      await new Promise((resolve) => setTimeout(resolve, 150));

      const appElement = this.mountPoint.querySelector("#app");
      if (appElement && (appElement.__vue_app__ || appElement.__vue__)) {
        this.instance = appElement.__vue_app__ || appElement.__vue__;
      }
      const loader = this.modal.querySelector("#editor-loading");
      if (loader) loader.classList.add("is-hidden");
    }
    close() {
      this.modal.classList.remove("is-open");
      this.modal.classList.add("is-closing");
      setTimeout(() => {
        this.modal.classList.remove("is-closing");
        this.modal.close();
      }, 300);
    }
  }

  function initEditorModal() {
    if (!window.EDITOR_DATA) return;
    if (window.editorModal) return;

    window.editorModal = new EditorModal();

    document.querySelectorAll("[data-open-editor]").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        window.editorModal.open();
      });
    });

    // Auto-open if redirected from cart (variation 2 only)
    const params = new URLSearchParams(window.location.search);
    if (params.get("openEditor") === "true") {
      window.editorModal.open();
    }
  }

  window.initEditorModal = initEditorModal;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initEditorModal);
  } else {
    initEditorModal();
  }
})();
